import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { xrayApi, XRayAnalysis } from '../api/xray';
import { API_URL } from '../api/client';

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const token = localStorage.getItem('token');
  const [isUploading, setIsUploading] = useState(false);
  const [confidence, setConfidence] = useState(0.25);
  const [currentAnalysis, setCurrentAnalysis] = useState<XRayAnalysis | null>(null);
  const [history, setHistory] = useState<XRayAnalysis[]>([]);
  const [error, setError] = useState('');
  const [showSegmentation, setShowSegmentation] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const response = await xrayApi.getList();
      setHistory(response.items);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, JPEG)');
      return;
    }

    setError('');
    setIsUploading(true);
    setCurrentAnalysis(null);

    try {
      const result = await xrayApi.upload(file, confidence);
      setCurrentAnalysis(result);
      loadHistory();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to analyze image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [confidence]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'pending': return 'status-pending';
      case 'processing': return 'status-pending';
      default: return 'status-failed';
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="app">
      <nav className="navbar">
        <a href="/dashboard" className="navbar-brand">Dental X-Ray Analysis</a>
        <div className="navbar-nav">
          <span className="nav-user">{user?.full_name || user?.email}</span>
          <button onClick={logout} className="btn btn-secondary btn-sm">Logout</button>
        </div>
      </nav>

      <div className="dashboard">
        <div className="dashboard-header">
          <h1 className="dashboard-title">X-Ray Analysis</h1>
        </div>

        {/* Upload Section */}
        <div className="upload-section">
          <h2 style={{ marginBottom: '1rem' }}>Upload X-Ray Image</h2>

          {error && <div className="error-message">{error}</div>}

          <div
            className={`upload-area ${isDragging ? 'dragging' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            {isUploading ? (
              <>
                <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
                <p>Analyzing X-ray...</p>
              </>
            ) : (
              <>
                <div className="upload-icon">📤</div>
                <p className="upload-text">Drag and drop your X-ray image here</p>
                <p className="upload-hint">or click to browse (PNG, JPG, JPEG)</p>
              </>
            )}
          </div>

          <input
            id="file-input"
            type="file"
            accept="image/*"
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />

          <div className="confidence-slider">
            <div className="slider-label">
              <span>Confidence Threshold</span>
              <span>{(confidence * 100).toFixed(0)}%</span>
            </div>
            <input
              type="range"
              className="slider-input"
              min="0.1"
              max="0.9"
              step="0.05"
              value={confidence}
              onChange={(e) => setConfidence(parseFloat(e.target.value))}
            />
          </div>
        </div>

        {/* Current Analysis Results */}
        {currentAnalysis && (
          <div className="results-section">
            <h2 style={{ marginBottom: '1.5rem' }}>Analysis Results</h2>

            <div className="summary-card">
              <p className="summary-title">Issues Detected</p>
              <p className="summary-value">{currentAnalysis.num_detections}</p>
            </div>

            <div className="results-grid results-grid-large">
              <div className="image-container">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <h3 style={{ margin: 0 }}>{showSegmentation ? 'Segmentation Result' : 'Original Image'}</h3>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowSegmentation(!showSegmentation)}
                    >
                      {showSegmentation ? 'Show Original' : 'Show Segmentation'}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setIsFullscreen(true)}
                      title="View fullscreen"
                    >
                      ⛶ Fullscreen
                    </button>
                  </div>
                </div>
                <img
                  src={showSegmentation
                    ? `${API_URL}/api/xray/${currentAnalysis.id}/result?token=${token}`
                    : `${API_URL}/api/xray/${currentAnalysis.id}/image?token=${token}`
                  }
                  alt={showSegmentation ? 'Segmentation Result' : 'Original X-Ray'}
                  className="result-image result-image-large"
                  onClick={() => setIsFullscreen(true)}
                  style={{ cursor: 'pointer' }}
                />
              </div>

              <div className="result-details">
                <h3>Detected Issues</h3>
                {currentAnalysis.class_counts && Object.keys(currentAnalysis.class_counts).length > 0 ? (
                  <ul className="detection-list">
                    {Object.entries(currentAnalysis.class_counts).map(([className, count]) => (
                      <li key={className} className="detection-item">
                        <span className="detection-class">{className}</span>
                        <span className="detection-count">{count}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ color: '#6b7280', padding: '1rem 0' }}>No issues detected</p>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  <p><strong>X-Ray Type:</strong> {currentAnalysis.xray_type || 'Unknown'}</p>
                  <p><strong>Status:</strong> {currentAnalysis.status}</p>
                  <p style={{ marginTop: '0.5rem', color: '#6b7280' }}>{currentAnalysis.summary}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fullscreen Modal */}
        {isFullscreen && currentAnalysis && (
          <div className="fullscreen-overlay" onClick={() => setIsFullscreen(false)}>
            <div className="fullscreen-content" onClick={(e) => e.stopPropagation()}>
              <div className="fullscreen-header">
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowSegmentation(!showSegmentation)}
                  >
                    {showSegmentation ? 'Show Original' : 'Show Segmentation'}
                  </button>
                </div>
                <button
                  className="btn btn-secondary btn-sm fullscreen-close"
                  onClick={() => setIsFullscreen(false)}
                >
                  ✕ Close
                </button>
              </div>
              <img
                src={showSegmentation
                  ? `${API_URL}/api/xray/${currentAnalysis.id}/result?token=${token}`
                  : `${API_URL}/api/xray/${currentAnalysis.id}/image?token=${token}`
                }
                alt={showSegmentation ? 'Segmentation Result' : 'Original X-Ray'}
                className="fullscreen-image"
              />
            </div>
          </div>
        )}

        {/* History Section */}
        <div className="history-section">
          <h2 className="history-title">Analysis History</h2>

          {history.length > 0 ? (
            <table className="history-table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Type</th>
                  <th>Detections</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item) => (
                  <tr key={item.id}>
                    <td>{item.original_filename}</td>
                    <td>{item.xray_type || '-'}</td>
                    <td>{item.num_detections}</td>
                    <td>
                      <span className={`status-badge ${getStatusClass(item.status)}`}>
                        {item.status}
                      </span>
                    </td>
                    <td>{formatDate(item.created_at)}</td>
                    <td>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setCurrentAnalysis(item)}
                        style={{ marginRight: '0.5rem' }}
                      >
                        View
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={async () => {
                          if (window.confirm('Delete this analysis?')) {
                            await xrayApi.delete(item.id);
                            loadHistory();
                            if (currentAnalysis?.id === item.id) {
                              setCurrentAnalysis(null);
                            }
                          }
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <p>No analyses yet. Upload an X-ray to get started.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
